import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { invariant } from '@mimir/common/util/invariant';
import { assert } from 'chai';
import { deliverPremiereItemUpdate } from './deliverPremiereItemUpdate';
import type { ProjectItemUpdate } from '@mimir/primitive/nle/update';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import { getClipKey } from '@mimir/primitive/nle/clip';
import { refreshClipLocalMediaStates } from './syncing';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import { premiere } from '../globals/premiere';
import type { NleProjectItemReference } from '@mimir/primitive/nle/reference';
import { MIMIR_ITEM_ID_KEY } from '@mimir/primitive/nle/metadata';

const refreshClipLocalMediaStatesWithRef = async (
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    localStateOps: LocalStateOps,
  },
  itemRef: NleProjectItemReference,
) => {
  // I tried prop drill for premiere.clipInfos but got lost after changing about 35 files.
  // I found myself in functions where I was passing this parameter that had nothing to do
  // with it. Also I was confused about what to do in shared code between web/desktop
  // situations where clipInfos should not even exist. I was also passing the parameter
  // through store dispatch methods that didn't seem to be typesafe anyway. So I abandoned
  // this approach.
  // Another thought crossed my mind to store clipInfos in applicationContext. But that
  // would require to move many typescript definitions related to Premiere from
  // clients/mimir-common to common because the context definitions are in common. I
  // am not sure why these Premiere types are split between mimir-common and primitive/premiere
  // in the first place. So I am afraid that could result in some other cardinal sin.
  // I ended up just importing premiere from globals for now.
  const allClipInfos = premiere.clipInfos;
  const clipKey = getClipKey(itemRef.itemNodeId, itemRef.projectRef.path);
  const clipInfo = allClipInfos[clipKey];
  if (!clipInfo) {
    return;
  }
  await refreshClipLocalMediaStates(services, clipInfo, true);
};

export async function updateExistingClip(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    localStateOps: LocalStateOps,
  },
  ongoingDownload: OngoingDownload,
  locallyAvailablePath: string,
) {
  const adobeContext = services.applicationContext;
  const deliverArr = ongoingDownload.deliver;
  assert(deliverArr, 'Bad assumption. Not all completed downloads appear to have delivery strategies.');
  const premiereItemUpdateDeliverArr = (deliverArr ?? []).filter(d => d.type === 'DeliverPremiereItemUpdate');
  if (premiereItemUpdateDeliverArr.length > 0) {
    for (const deliver of premiereItemUpdateDeliverArr) {
      await deliverPremiereItemUpdate(adobeContext, ongoingDownload, deliver, locallyAvailablePath);
      await refreshClipLocalMediaStatesWithRef(services, deliver.itemRef);
    }
    return;
  }

  const itemId = ongoingDownload.itemId;
  invariant(itemId, 'Expected itemId in updateExistingClip');
  const artefact = ongoingDownload.artefact;
  invariant(artefact !== undefined, 'Expected artefact in updateExistingClip');
  const cepb = adobeContext.cepBridgeClient;
  const projectItemInfos = await cepb.call('getAllProjectItemInfos');
  for (const projectItemInfo of projectItemInfos) {
    if (projectItemInfo.meta[MIMIR_ITEM_ID_KEY] !== itemId) {
      continue;
    }
    const update : ProjectItemUpdate = { meta: { } };
    if (artefact === 'proxy') {
      update.proxyMediaPath = locallyAvailablePath;
    }
    if (artefact === 'highres') {
      update.hiresMediaPath = locallyAvailablePath;
    }
    await cepb.call('updateProjectItem', projectItemInfo.itemRef, update);
    await refreshClipLocalMediaStatesWithRef(services, projectItemInfo.itemRef);
  }
}
